/* @flow */

import pubsub from './pubsub';
import {
  getSupportedVendorPrefix,
  getVendorJsPropertyName
} from './browserutil';

import { isValid } from './coreutils';
import { logDebug } from './logger';

export const VISIBILITYCHANGE_EVENT = 'visibilitychange';

/**
 * @module utils/visibilityhelper
 * @requires utils/coreutils
 * @requires utils/logger
 */

export const VisibilityState = {
  /**
   * The document is being pre-rendered before being shown.
   */
  PRERENDER: 'prerender',

  /**
   * The document is currently active and visible.
   */
  VISIBLE: 'visible',

  /**
   * The document is active but the browser tab or AMP app is not.
   */
  HIDDEN: 'hidden',

  /**
   * The document is visible, but the user has started swiping away from
   * it. The runtime may stop active playback.
   */
  PAUSED: 'paused',

  /**
   * The document is no longer active because the user swiped away or
   * closed the viewer. The document may become visible again later.
   */
  INACTIVE: 'inactive'
};

class VisibilityHelper {
  _name: string;

  _doc: Document;

  _bInited: boolean;

  _hiddenProp: string;

  _visState: string;

  _changeEvent: string;

  _isDocHidden: boolean;

  _hiddenStartTime: number;

  /**
   * @class
   * @description Helper class to get visibility state of the top window
   */
  constructor() {
    this._name = 'vishelper';
    this._bInited = false;
    this._isDocHidden = false;
    this._hiddenStartTime = 0;
  }

  /**
   * @description Initialize the object
   * @param {Window} win Window Object
   * @param {*} doc Document object
   * @returns {void}
   */
  init(win: WindowProxy, doc: Document = window.document) {
    if (this._bInited) {
      return;
    }
    this._doc = doc;
    // get required properties
    const prefix = getSupportedVendorPrefix(this._doc, 'hidden');
    this._hiddenProp = getVendorJsPropertyName(this._doc, 'hidden');
    this._visState = getVendorJsPropertyName(this._doc, 'visibilityState');
    const evtName = `${prefix}visibilitychange`;

    // attach the event if supported
    // $FlowFixMe[method-unbinding]
    if (this._doc.addEventListener) {
      this._doc.addEventListener(
        evtName,
        () => {
          this._publishState(
            this.getVisibilityState() !== VisibilityState.VISIBLE
          );
        },
        false
      );
      // Experimental. Use focus/blur event for app switch and older browsers
      win.addEventListener(
        'focus',
        () => {
          this._publishState(false);
        },
        false
      );
      win.addEventListener(
        'blur',
        () => {
          this._publishState(true);
        },
        false
      );
    }
    this._bInited = true;
  }

  /**
   * @description Get visibility state
   * @returns {string} Current state
   */
  getVisibilityState(): string {
    const visState = getVendorJsPropertyName(this._doc, 'visibilityState');
    // $FlowFixMe[prop-missing]
    if (isValid(this._doc[this._visState])) {
      // $FlowFixMe[prop-missing]
      return this._doc[visState];
    }

    // old browsers
    // $FlowFixMe[prop-missing]
    if (isValid(this._doc[this._hiddenProp])) {
      return this._doc[this._hiddenProp]
        ? VisibilityState.HIDDEN
        : VisibilityState.VISIBLE;
    }

    return VisibilityState.VISIBLE;
  }

  /**
   * @returns {boolean} Is the window currently hidden?
   */
  isDocHidden(): boolean {
    return this._isDocHidden;
  }

  /**
   *
   * Returns how long the document is hidden
   *
   * @returns {number} How long document is idle (in seconds)
   */
  isHiddenFor(): number {
    if (!this._isDocHidden) {
      return 0;
    }
    return Math.floor((Date.now() - this._hiddenStartTime) / 1000);
  }

  /**
   *
   * @private
   * @param {boolean} isDocHidden Updated status
   * @returns {void}
   */
  _publishState(isDocHidden: boolean) {
    this._isDocHidden = isDocHidden;
    if (isDocHidden) {
      this._hiddenStartTime = Date.now();
    }
    logDebug(
      `${this._name} EVT: changed to ${isDocHidden ? 'hidden' : 'visible'}`
    );
    pubsub.emit(VISIBILITYCHANGE_EVENT, isDocHidden);
  }
}

const vis: VisibilityHelper = new VisibilityHelper();
export default vis;
